import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { ReactNode } from "react";

interface NotificationContextState {
  notifications: Notification[];
  updateNotifications: (notifications: Notification[]) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const NotificationContext = createContext<NotificationContextState | undefined>(
  undefined,
);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const updateNotifications = useCallback(
    (newNotifications: Notification[]) => {
      setNotifications(newNotifications);
    },
    [],
  );

  const value = useMemo(
    () => ({
      notifications,
      updateNotifications,
      loading,
      setLoading,
    }),
    [notifications, updateNotifications, loading, setLoading],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext(): NotificationContextState {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider",
    );
  }
  return context;
}
